import { Api, buildFormData } from '@deepcloud/api-base'
import { mergeConfig } from '@deepcloud/http-client'
import type {
  HttpClient,
  HttpClientRequestConfig,
} from '@deepcloud/http-client'
import type {
  OperationsGetTreeParams,
  OperationsListCasesParams,
  OperationsUploadFileFormData,
  OpsAnalysisResponse,
  OpsAnalysisSaveRequest,
  OpsCaseOverviewResponse,
  OpsCaseResponse,
  OpsCaseSaveRequest,
  OpsFileInfo,
  OpsNodeCreate,
  OpsNodeMove,
  OpsNodeRename,
  OpsTreeResponse,
} from '../../@types'

export class OperationsAPI extends Api {
  constructor(httpClient: HttpClient) {
    super(httpClient)
  }

  /**
   * Get Operations Tree
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async getTree(
    params?: OperationsGetTreeParams,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/operations/tree`

    return this.httpClient.get<OpsTreeResponse>(
      path,
      mergeConfig({ params }, config),
    )
  }

  /**
   * Post Operations Node
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async postNode(payload: OpsNodeCreate, config?: HttpClientRequestConfig) {
    const path = `/operations/tree/node`

    return this.httpClient.post<Record<string, unknown>>(path, payload, config)
  }

  /**
   * Rename Operations Node
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async renameNode(
    nodeId: number,
    payload: OpsNodeRename,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/operations/tree/rename/${nodeId}`

    return this.httpClient.post<Record<string, unknown>>(path, payload, config)
  }

  /**
   * Move Operations Node
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async moveNode(
    nodeId: number,
    payload: OpsNodeMove,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/operations/tree/move/${nodeId}`

    return this.httpClient.post<Record<string, unknown>>(path, payload, config)
  }

  /**
   * Delete Operations Node
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async deleteNode(nodeId: number, config?: HttpClientRequestConfig) {
    const path = `/operations/tree/node/${nodeId}`

    return this.httpClient.delete<Record<string, unknown>>(path, config)
  }

  /**
   * List Operations Cases
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async listCases(
    params?: OperationsListCasesParams,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/operations/cases`

    return this.httpClient.get<OpsCaseOverviewResponse>(
      path,
      mergeConfig({ params }, config),
    )
  }

  /**
   * Get Operations Case
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async getCase(caseId: number, config?: HttpClientRequestConfig) {
    const path = `/operations/case/${caseId}`

    return this.httpClient.get<OpsCaseResponse>(path, config)
  }

  /**
   * Delete Operations Case
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async deleteCase(caseId: number, config?: HttpClientRequestConfig) {
    const path = `/operations/case/${caseId}`

    return this.httpClient.delete<Record<string, unknown>>(path, config)
  }

  /**
   * Save Operations Case
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async saveCase(
    payload: OpsCaseSaveRequest,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/operations/case`

    return this.httpClient.post<Record<string, unknown>>(path, payload, config)
  }

  /**
   * Add Operations Analysis
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async addAnalysis(
    caseId: number,
    payload: OpsAnalysisSaveRequest,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/operations/case/${caseId}/analysis`

    return this.httpClient.post<OpsAnalysisResponse>(path, payload, config)
  }

  /**
   * Save Operations Analysis
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async saveAnalysis(
    caseId: number,
    analysisId: number,
    payload: OpsAnalysisSaveRequest,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/operations/case/${caseId}/analysis/${analysisId}`

    return this.httpClient.post<OpsAnalysisResponse>(path, payload, config)
  }

  /**
   * Delete Operations Analysis
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async deleteAnalysis(
    caseId: number,
    analysisId: number,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/operations/case/${caseId}/analysis/${analysisId}`

    return this.httpClient.delete<Record<string, unknown>>(path, config)
  }

  /**
   * Upload Operations File
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async uploadFile(
    caseId: number,
    analysisId: number,
    payload: OperationsUploadFileFormData,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/operations/case/${caseId}/analysis/${analysisId}/file`

    return this.httpClient.post<OpsFileInfo>(
      path,
      buildFormData(payload),
      config,
    )
  }

  /**
   * Delete Operations File
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async deleteFile(
    caseId: number,
    analysisId: number,
    filename: string,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/operations/case/${caseId}/analysis/${analysisId}/file/${filename}`

    return this.httpClient.delete<Record<string, unknown>>(path, config)
  }
}
